import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import globalSettingsBranches from '../../../../../../../stores/global-settings-branches';
import { Calendar } from 'primereact/calendar';
import { BranchLecturerRatesGroupDB } from '../../../../../../../api/types/lecturers/user-details-lecturer';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import dateFormat from '../../../../../../../utils/date-format';
import globalUserPage from '../../../../../../../stores/global-user-page';
import { SettingsBranches } from '../../../../../../../api/types/settings/branches/settings-branches';
import { addManualWorkingHoursOperation } from './operations/add-manual-working-hours-operation';
import { WorkingHoursResponse } from '../../../../../../../api/types/lecturers/working-hours/working-hours.type';

export interface AddManualWorkingHoursDialogDatamodel {
  userId: number;
  showAddDialog: boolean;
  setShowAddDialog: (showAddDialog: boolean) => any;
  editData?: WorkingHoursResponse | null;
}

interface ActivityTypeWithRate {
  rateGroup: BranchLecturerRatesGroupDB;
  rateId: number;
  disabled?: boolean;
}

export const AddManualWorkingHoursDialog: React.FC<
  AddManualWorkingHoursDialogDatamodel
> = ({ userId, showAddDialog, setShowAddDialog, editData }) => {
  const [date, setDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [selectedActivityType, setSelectedActivityType] =
    useState<ActivityTypeWithRate | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<SettingsBranches | null>(
    null,
  );

  const isEditMode = !!editData;

  // Get available activity types from global user page
  const availableActivityTypes = globalUserPage.lecturerDetails?.rates3 || [];

  // Reset form when dialog opens/closes or edit data changes
  useEffect(() => {
    if (showAddDialog && editData) {
      // Pre-fill form with edit data
      const editStartDate = new Date(editData.startAt);
      const editEndDate = new Date(editData.endAt);

      setDate(editStartDate);

      // Create time objects for start and end times
      const startTimeObj = new Date();
      startTimeObj.setHours(
        editStartDate.getHours(),
        editStartDate.getMinutes(),
        0,
        0,
      );
      setStartTime(startTimeObj);

      const endTimeObj = new Date();
      endTimeObj.setHours(
        editEndDate.getHours(),
        editEndDate.getMinutes(),
        0,
        0,
      );
      setEndTime(endTimeObj);

      // Find matching branch and activity type
      const matchingBranch = globalSettingsBranches.branches.find((branch) => {
        // We need to find the branch that contains the activity type used in this working hour
        const matchingActivityType = availableActivityTypes.find(
          (actType) =>
            actType.settingsActivityTypeId === editData.activityTypeId &&
            actType.branchId === branch.id,
        );
        return !!matchingActivityType;
      });

      if (matchingBranch) {
        setSelectedBranch(matchingBranch);

        // Find matching activity type
        const matchingActivityType = availableActivityTypes.find(
          (actType) =>
            actType.settingsActivityTypeId === editData.activityTypeId &&
            actType.branchId === matchingBranch.id,
        );

        if (matchingActivityType) {
          // Find the rate that was used
          const matchingRate = matchingActivityType.rates.find(
            (rate) => rate.rate === editData.branchLecturersRates,
          );

          if (matchingRate) {
            setSelectedActivityType({
              rateGroup: matchingActivityType,
              rateId: matchingRate.id,
              disabled: false,
            });
          }
        }
      }
    } else if (showAddDialog && !editData) {
      // Reset form for add mode
      setDate(null);
      setStartTime(null);
      setEndTime(null);
      setSelectedActivityType(null);
      setSelectedBranch(null);
    }
  }, [showAddDialog, editData, availableActivityTypes]);

  // Filter activity types based on selected date and branch
  const getFilteredActivityTypes = (): ActivityTypeWithRate[] => {
    if (!date || !selectedBranch) {
      // If no date or branch selected, return all activity types but mark them as disabled
      return availableActivityTypes.map((activityType) => ({
        rateGroup: activityType,
        rateId: -1, // Invalid rate ID for disabled items
        disabled: true,
      }));
    }

    const selectedDate = date;
    const selectedBranchId = selectedBranch.id;

    return availableActivityTypes
      .filter((activityType) => activityType.branchId === selectedBranchId) // Filter by branch
      .map((activityType) => {
        // Find the rate that covers the selected date
        const validRate = activityType.rates.find((rate) => {
          const rateStartDate = new Date(rate.startTime);
          const rateEndDate = new Date(rate.endTime);

          // Check if selected date falls within rate's date range
          return selectedDate >= rateStartDate && selectedDate <= rateEndDate;
        });

        // In edit mode, allow all activity types to be selectable
        // In add mode, only allow activity types with valid rates
        return {
          rateGroup: activityType,
          rateId: validRate ? validRate.id : -1,
          disabled: isEditMode ? false : !validRate,
        };
      });
  };

  const filteredActivityTypes = getFilteredActivityTypes();

  const handleAddWorkingHours = async () => {
    // Validate all required fields
    if (
      !date ||
      !startTime ||
      !endTime ||
      !selectedActivityType ||
      !selectedBranch
    ) {
      toast.warn('יש למלא את כל השדות');
      return;
    }

    await addManualWorkingHoursOperation(
      userId,
      selectedBranch.id,
      selectedActivityType.rateGroup.settingsActivityTypeId ?? null,
      selectedActivityType.rateId,
      date,
      startTime,
      endTime,
      isEditMode ? editData?.id : null, // Pass ID for edit mode
    );

    // Reset form and close dialog
    setDate(null);
    setStartTime(null);
    setEndTime(null);
    setSelectedActivityType(null);
    setSelectedBranch(null);
    setShowAddDialog(false);
  };

  const handleBranchChange = (newBranch: SettingsBranches) => {
    setSelectedBranch(newBranch);
    // Reset activity type when branch changes since availability changes
    setSelectedActivityType(null);
  };

  const handleDateChange = (newDate: Date) => {
    setDate(newDate);
    // Reset activity type when date changes since availability might change
    setSelectedActivityType(null);
  };

  const handleDialogClose = () => {
    // Reset form when dialog closes
    setDate(null);
    setStartTime(null);
    setEndTime(null);
    setSelectedActivityType(null);
    setSelectedBranch(null);
    setShowAddDialog(false);
  };

  return (
    <Dialog
      visible={showAddDialog}
      onHide={handleDialogClose}
      header={isEditMode ? 'עריכת שעות עבודה' : 'הוספת שעות עבודה'}
      modal
      style={{ width: '500px' }}
      footer={
        <div
          style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
        >
          <Button
            label="ביטול"
            icon="pi pi-times"
            outlined
            onClick={handleDialogClose}
          />
          <Button
            label={isEditMode ? 'עדכן' : 'הוסף'}
            icon={isEditMode ? 'pi pi-save' : 'pi pi-check'}
            onClick={handleAddWorkingHours}
          />
        </div>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px 0',
        }}
      >
        {/* Branch Selection */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            שלוחה
          </label>
          <Dropdown
            value={selectedBranch}
            onChange={(e) => handleBranchChange(e.value)}
            options={globalSettingsBranches.branches}
            optionLabel="branchName"
            placeholder="בחר שלוחה"
            style={{ width: '100%' }}
            emptyMessage="אין שלוחות זמינות"
            disabled={isEditMode}
          />
        </div>

        {/* Date Input */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            תאריך
          </label>
          <Calendar
            value={date}
            onChange={(e) => handleDateChange(e.value as Date)}
            dateFormat="dd/mm/yy"
            showIcon
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            panelStyle={{ direction: 'ltr' }}
            disabled={isEditMode ? false : !selectedBranch}
          />
        </div>

        {/* Start Time Input */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            שעת התחלה
          </label>
          <Calendar
            value={startTime}
            onChange={(e) => setStartTime(e.value as Date)}
            timeOnly
            hourFormat="24"
            showIcon
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            panelStyle={{ direction: 'ltr' }}
          />
        </div>

        {/* End Time Input */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            שעת סיום
          </label>
          <Calendar
            value={endTime}
            onChange={(e) => setEndTime(e.value as Date)}
            timeOnly
            hourFormat="24"
            showIcon
            style={{ width: '100%', direction: 'ltr' }}
            inputStyle={{ direction: 'rtl', textAlign: 'right' }}
            panelStyle={{ direction: 'ltr' }}
          />
        </div>

        {/* Activity Type Dropdown */}
        <div>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            סוג פעילות
          </label>
          <Dropdown
            value={selectedActivityType}
            onChange={(e) => setSelectedActivityType(e.value)}
            options={filteredActivityTypes}
            optionDisabled="disabled"
            itemTemplate={(option: ActivityTypeWithRate) => (
              <div
                style={{
                  color: option.disabled ? '#ccc' : 'inherit',
                  opacity: option.disabled ? 0.6 : 1,
                }}
              >
                {option.rateGroup.activityName}
                {option.disabled && (
                  <span style={{ fontSize: '12px', marginRight: '8px' }}>
                    (לא זמין בתאריך זה)
                  </span>
                )}
                {!option.disabled && (
                  <span
                    style={{
                      fontSize: '12px',
                      marginRight: '8px',
                      color: '#666',
                    }}
                  >
                    (מזהה תעריף: {option.rateId})
                  </span>
                )}
              </div>
            )}
            valueTemplate={(option: ActivityTypeWithRate) =>
              option ? option.rateGroup.activityName : 'בחר סוג פעילות'
            }
            placeholder={
              date && selectedBranch
                ? 'בחר סוג פעילות'
                : 'בחר שלוחה ותאריך תחילה'
            }
            style={{ width: '100%' }}
            emptyMessage="אין סוגי פעילות זמינים"
          />
        </div>
      </div>
    </Dialog>
  );
};
